import React from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useVeryRecentSnapshots } from "../../fetch/fetchMaintenance";
import { Alert, Autocomplete, Box, Button, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, useTheme } from "@mui/material";
import { DateField } from '@mui/x-date-pickers/DateField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { usePhoneMode } from "../../hooks";
import { useServiceTypes } from "../../fetch/fetchMaintenance";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider";
import dayjs from "dayjs";
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { useFetchDealerBasic } from "../../fetch/userFetch";
import Spinner from "../spinner/Spinner";

export default function AddEditServiceLogUI({
    model,
    handleBaseFieldValueChanged,
    handleEngineFieldValueChanged,
    handleAddEngineClick,
    handleRemoveEngineClick,
    isEdit,
    children,
    inMercNet,
    locked,
    isAdd,
    notFound
}) {
    const { id } = useParams();
    const theme = useTheme();
    const scrollViewerTop = theme.appBar.height;
    const { isMobile } = usePhoneMode();
    const navigate = useNavigate();

    const { items } = useVeryRecentSnapshots({ isEnabled: !id || id === 'add', pageSize: 3, minutes: 60 })

    let impactedSerials = '';
    if (!!model.engines) {
        impactedSerials = model.engines.filter(e => e.impacted).map(e => e.engineSerial).join(', ');
    }
    const { data: sourceServiceTypes, isLoading } = useServiceTypes(impactedSerials);
    const { data: dealerInfo } = useFetchDealerBasic(model?.dealerNumber);
    const isDealerInMercNet = useSelector((state) => state.permissions.isInMercNet);

    const serviceTypes = sourceServiceTypes.filter(it => !isAdd || !it.existsInMercNet);
    const completedServiceTypes = sourceServiceTypes.filter(it => it.existsInMercNet);
    const allCompleted = isAdd && !isLoading && sourceServiceTypes?.length > 0 && completedServiceTypes?.length === sourceServiceTypes?.length;

    if (!handleBaseFieldValueChanged) {
        handleBaseFieldValueChanged = () => { };
    }

    if (!handleEngineFieldValueChanged) {
        handleEngineFieldValueChanged = () => { };
    }

    const {
        dateOfService, hullId,
        serviceType, technicianName, workOrderNumber,
        comments, engines, sourceSnapshotId, techId
    } = model || {};
    const isManualEdit = !sourceSnapshotId && isEdit;

    const hasEngineSerials = engines?.filter(engine => !!engine.engineSerial).map(engine => engine.engineSerial)?.length == engines?.length;

    const locationItems = [
        'STBD Engine',
        'PORT Engine',
        'STBD Inner Engine',
        'PORT Inner Engine',
        'STBD Outer Engine',
        'PORT Outer Engine'
    ];
    const usedLocations = engines?.map(engine => engine.engineLocation) || [];

    return (
        <Grid
            id='scroll-viewer'
            container
            style={{
                position: 'fixed',
                top: scrollViewerTop,
                left: 0,
                height: `calc(100vh - ${scrollViewerTop}px)`,
                overflowX: 'hidden',
                overflowY: 'hidden',
                width: '100vw',
                padding: '1.5em 1em 1.5em 1em'
            }}
        >
            <Grid item xs>
                <Paper
                    sx={{
                        p: 1.5,
                        height: '100%',
                        marginLeft: '1em',
                        marginTop: '.5em',
                        marginBottom: '1em',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        maxHeight: `calc(100vh - ${scrollViewerTop + (isMobile ? 36 : 0)}px - 2em)`
                    }}
                >
                    <Box
                        component="form"
                        sx={{
                            '& .MuiTextField-root': { m: 1, width: 'calc(100% - 16px)' },
                            '& .MuiFormControl-root': { m: 1, width: 'calc(100% - 16px)' },
                            '& .MuiAutocomplete-root': { width: '100%' }
                        }}
                        autoComplete="off"
                    >
                        <Grid container columns={{ xs: 1, lg: 2 }}>
                            <Grid item xs={2}>
                                <Grid container columns={{ xs: 1, sm: 2, md: 6, lg: 8, xl: 12 }}>
                                    <Grid item xs={12}>
                                        <h3>Service Log</h3>
                                    </Grid>
                                    {notFound ? (
                                        <Grid item xs={12}>
                                            <Alert sx={{ m: 2 }} severity="warning">Scan was not found, engine info can be manually entered.</Alert>
                                        </Grid>
                                    ) : null}
                                    {!!items && items?.length > 0 && (
                                        <Grid item xs={12}>
                                            <Typography>Found {items.length} recent snapshot{items.length !== 1 ? 's' : ''} click Use to set as a starting point.</Typography>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Scan Time</TableCell>
                                                        <TableCell>Hull Id</TableCell>
                                                        <TableCell>Serial Number(s)</TableCell>
                                                        <TableCell></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {items.map((item, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{dayjs.utc(item.snapshotDate).local().format('MM/DD/YYYY hh:mm A')}</TableCell>
                                                            <TableCell>{item.hullId}</TableCell>
                                                            <TableCell>{item.correctedEngineSerialNumbers}</TableCell>
                                                            <TableCell>
                                                                <Button onClick={() => navigate(`/literature/service/${item.sourceSnapshotId}/log`)}>Use</Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Grid>
                                    )}
                                    {isDealerInMercNet ? (
                                        <>
                                            <Grid item xs={12}>
                                                {!inMercNet ? <Alert sx={{ m: 2 }} severity="info">{!allCompleted ? 'Service log entries will also be available in MercNET.' : 'All service log entries have been completed.'}</Alert> : null}
                                                {inMercNet && !locked ? (
                                                    <Alert sx={{ m: 2 }} severity="info">This service log is also saved to MercNET.</Alert>
                                                ) : null}
                                            </Grid>
                                            {allCompleted ? (
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Date</TableCell>
                                                            <TableCell>Service Type</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {completedServiceTypes.map((service) => (
                                                            <TableRow key={service.id}>
                                                                <TableCell>{dayjs(service.dateOfService).format('MM/DD/YYYY')}</TableCell>
                                                                <TableCell>{service.value}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            ) : null}
                                        </>
                                    ) : null}
                                    {inMercNet && locked ? (
                                        <Grid item xs={12}>
                                            <Alert sx={{ m: 2 }} severity="warning">This service log entry was added to MercNET by another dealer and cannot be updated.</Alert>
                                        </Grid>
                                    ) : null}
                                    {!allCompleted ? (
                                        <>
                                            <Grid item xs={1} md={2}>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DateField
                                                        required={!sourceSnapshotId}
                                                        error={!dateOfService}
                                                        readOnly={!isEdit}
                                                        id='dateOfService'
                                                        label='Date of Service'
                                                        value={dayjs(dateOfService || '')}
                                                        onChange={(newValue) => { handleBaseFieldValueChanged('dateOfService', newValue) }}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                            {isDealerInMercNet && !hasEngineSerials ? (
                                                <Grid item xs={1} md={2}>
                                                    <Alert sx={{ m: 2 }} severity="error">Engine serial{engines.length > 1 ? 's' : ''} needs to be entered first.</Alert>
                                                </Grid>
                                            ) : null}
                                            {isDealerInMercNet && hasEngineSerials ? (
                                                <Grid item xs={1} md={2}>
                                                    {isLoading ? (
                                                        <Spinner width={70} />
                                                    ) : (
                                                        <FormControl fullWidth>
                                                            <InputLabel id='service-type-select-label' error={!serviceType} required>Service Type</InputLabel>
                                                            <Select
                                                                labelId='service-type-select-label'
                                                                id='service-type-select-label'
                                                                value={serviceType || ''}
                                                                error={!serviceType}
                                                                label="Service Type"
                                                                onChange={(event) => { handleBaseFieldValueChanged('serviceType', event.target.value) }}
                                                                sx={{ width: '100%' }}
                                                            >
                                                                {serviceTypes.map(item => {
                                                                    return (
                                                                        <MenuItem key={item?.value} value={item?.value}>{item?.value}</MenuItem>
                                                                    );
                                                                })}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </Grid>
                                            ) : null}
                                            {!isDealerInMercNet ? (
                                                <Grid item xs={1} md={2}>
                                                    {isLoading ? (
                                                        <Spinner width={70} />
                                                    ) : (
                                                        <Autocomplete
                                                            freeSolo
                                                            options={(serviceTypes || []).map(it => it.value)}
                                                            onChange={(event, value) => { handleBaseFieldValueChanged('serviceType', value) }}
                                                            value={serviceType || ''}
                                                            readOnly={!isEdit}
                                                            renderInput={(params) => <TextField
                                                                {...params}
                                                                required
                                                                label="Service Type"
                                                                error={!serviceType}
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    maxLength: 200
                                                                }}
                                                                value={serviceType || ''}
                                                                onChange={(event) => { handleBaseFieldValueChanged('serviceType', event.target.value) }}
                                                            />}
                                                        />
                                                    )}
                                                </Grid>
                                            ) : null}
                                            {!!dealerInfo ? (
                                                <Grid item xs={1} md={2}>
                                                    <TextField
                                                        id='dealerName'
                                                        label='Dealer Name'
                                                        slotProps={{ htmlInput: { readOnly: true } }}
                                                        value={dealerInfo.name || ''}
                                                    />
                                                </Grid>
                                            ) : null}
                                            <Grid item xs={1} md={2}>
                                                <TextField
                                                    id='hullId'
                                                    label='Hull ID'
                                                    slotProps={{ htmlInput: { maxLength: 100, readOnly: !isEdit } }}
                                                    value={hullId || ''}
                                                    onChange={(event) => { handleBaseFieldValueChanged('hullId', event.target.value) }}
                                                />
                                            </Grid>
                                            <Grid item xs={1} md={2}>
                                                <TextField
                                                    id='technicianName'
                                                    label='Technician Name'
                                                    slotProps={{ htmlInput: { maxLength: 200, readOnly: !isEdit } }}
                                                    value={technicianName || ''}
                                                    onChange={(event) => { handleBaseFieldValueChanged('technicianName', event.target.value) }}
                                                />
                                            </Grid>
                                            <Grid item xs={1} md={2}>
                                                <TextField
                                                    id='techId'
                                                    label='Technician ID'
                                                    slotProps={{ htmlInput: { maxLength: 50, readOnly: !isEdit } }}
                                                    value={techId || ''}
                                                    onChange={(event) => { handleBaseFieldValueChanged('techId', event.target.value) }}
                                                />
                                            </Grid>
                                            <Grid item xs={1} md={2}>
                                                <TextField
                                                    id='workOrderNumber'
                                                    label='Work Order Number'
                                                    slotProps={{ htmlInput: { maxLength: 100, readOnly: !isEdit } }}
                                                    value={workOrderNumber || ''}
                                                    onChange={(event) => { handleBaseFieldValueChanged('workOrderNumber', event.target.value) }}
                                                />
                                            </Grid>
                                            {/* <Grid item xs={12}>
                                        <TextField
                                            id='comments'
                                            label='Comments'
                                            slotProps={{ htmlInput: { maxLength: 4000, readOnly: !isEdit } }}
                                            value={comments || ''}
                                            multiline
                                            rows={4}
                                            onChange={(event) => { handleBaseFieldValueChanged('comments', event.target.value) }}
                                        />
                                    </Grid> */}
                                        </>) : null}
                                </Grid>
                            </Grid>
                            {!allCompleted ? (
                                <>
                                    {(engines || []).map((engine, index) => {
                                        const { impacted } = engine;
                                        const availableOptions = locationItems.filter(it => !usedLocations.includes(it) || it == engine.engineLocation);

                                        return (
                                            <Grid item xs={1} key={index}>
                                                <Grid container columns={{ xs: 1, sm: 2, md: 6 }}>
                                                    <Grid item xs={12}>
                                                        <h3 style={{ marginBottom: '0.25em' }}>
                                                            {!!engine.engineLocation ? engine.engineLocation : 'Engine'}: {engine.engineSerial}

                                                            {isManualEdit && isEdit && (engines || []).length > 1 ? (
                                                                <Button onClick={() => handleRemoveEngineClick(index)}><RemoveCircleIcon /></Button>
                                                            ) : null}
                                                        </h3>
                                                        {!!engine.moduleSerialNumber ? <span style={{ marginBottom: '0.25em' }}><strong>Module Serial Number:</strong> {engine.moduleSerialNumber}</span> : null}
                                                        {(engines.length > 1 || !impacted) && isEdit && !isManualEdit ? (
                                                            <Switch checked={impacted} onChange={(event) => handleEngineFieldValueChanged(index, 'impacted', event.target.checked)} readOnly={!isEdit} />
                                                        ) : null}
                                                    </Grid>
                                                    {isManualEdit && isEdit ? (
                                                        <Grid item xs={1} md={3}>
                                                            <FormControl fullWidth>
                                                                <InputLabel id='location-select-label' error={!engine.engineLocation} required>Location</InputLabel>
                                                                <Select
                                                                    labelId='location-select-label'
                                                                    id='location-select-label'
                                                                    value={engine.engineLocation}
                                                                    error={!engine.engineLocation}
                                                                    label="Location"
                                                                    onChange={(event) => { handleEngineFieldValueChanged(index, 'engineLocation', event.target.value) }}
                                                                    sx={{ width: '100%' }}
                                                                >
                                                                    {availableOptions.map(item => {
                                                                        return (
                                                                            <MenuItem key={item} value={item}>{item}</MenuItem>
                                                                        );
                                                                    })}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                    ) : null}
                                                    {impacted ? (
                                                        <>
                                                            <Grid item xs={1} md={3}>
                                                                <TextField
                                                                    id='engineSerial'
                                                                    label='Engine Serial'
                                                                    required
                                                                    slotProps={{ htmlInput: { maxLength: 50, readOnly: !isEdit } }}
                                                                    error={!engine.engineSerial}
                                                                    value={engine.engineSerial || ''}
                                                                    onChange={(event) => { handleEngineFieldValueChanged(index, 'engineSerial', event.target.value) }}
                                                                />
                                                            </Grid>
                                                            {isManualEdit ? (
                                                                <Grid item xs={1} md={3}>
                                                                    <TextField
                                                                        id='moduleSerialNumber'
                                                                        label='Module Serial Number'
                                                                        slotProps={{ htmlInput: { maxLength: 50, readOnly: !isEdit } }}
                                                                        value={engine.moduleSerialNumber || ''}
                                                                        onChange={(event) => { handleEngineFieldValueChanged(index, 'moduleSerialNumber', event.target.value) }}
                                                                    />
                                                                </Grid>
                                                            ) : null}
                                                            <Grid item xs={1} md={3}>
                                                                <TextField
                                                                    id='engineHours'
                                                                    label='Engine Hours'
                                                                    slotProps={{ htmlInput: { maxLength: 20, readOnly: !isEdit } }}
                                                                    value={engine.engineHours || ''}
                                                                    onChange={(event) => { handleEngineFieldValueChanged(index, 'engineHours', event.target.value) }}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <TextField
                                                                    id='comments'
                                                                    label='Engine Comments'
                                                                    slotProps={{ htmlInput: { maxLength: 4000, readOnly: !isEdit } }}
                                                                    value={engine.comments || ''}
                                                                    multiline
                                                                    rows={2}
                                                                    onChange={(event) => { handleEngineFieldValueChanged(index, 'comments', event.target.value) }}
                                                                />
                                                            </Grid>
                                                        </>
                                                    ) : (
                                                        <Grid item xs={12}>
                                                            <Typography style={{ paddingBottom: '1em' }}>Service doesn't apply to this engine</Typography>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        )
                                    })}
                                    {isManualEdit && isEdit && (engines || []).length < 6 ? (
                                        <Grid item xs={1}>
                                            <Button onClick={handleAddEngineClick}><AddIcon />Add Engine</Button>
                                        </Grid>
                                    ) : null}
                                </>) : null}
                        </Grid>
                        {!allCompleted ? children : null}
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
}